import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip, IconButton } from "@mui/material";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import AddIcon from "@mui/icons-material/Add";
import CalculateIcon from "@mui/icons-material/Calculate";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HistoryIcon from "@mui/icons-material/History";
import ExtensionIcon from "@mui/icons-material/Extension";
import ListIcon from "@mui/icons-material/List";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import ArchiveIcon from "@mui/icons-material/Archive";
import StorageIcon from "@mui/icons-material/Storage";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GavelIcon from "@mui/icons-material/Gavel";
import TimelineIcon from "@mui/icons-material/Timeline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COLORS } from "./dashboardConfig";

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_WIDTH_COLLAPSED = 68;

const useStyles = makeStyles((theme) => ({
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderRight: `1px solid ${COLORS.border}`,
    transition: "width 0.2s ease",
    overflow: "hidden",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    height: 64,
    padding: theme.spacing(0, 2),
    flexShrink: 0,
    borderBottom: `1px solid ${COLORS.borderSoft}`,
  },
  brandMark: {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: 8,
    background: COLORS.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandText: {
    fontSize: 18,
    fontWeight: 800,
    color: COLORS.primaryDark,
    whiteSpace: "nowrap",
    letterSpacing: 0.2,
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(1.5, 1.25),
  },
  groupTitle: {
    margin: theme.spacing(2, 1.5, 0.75),
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "#9e9e9e",
    whiteSpace: "nowrap",
  },
  groupDivider: {
    margin: theme.spacing(1.5, 1),
    borderTop: `1px solid ${COLORS.borderSoft}`,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    height: 40,
    padding: theme.spacing(0, 1.5),
    marginBottom: 2,
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#424242",
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background-color 0.15s, color 0.15s",
    "&:hover": { background: "#f5f5f5", color: COLORS.primaryDark },
    "& svg": { fontSize: 22, flexShrink: 0, color: "#757575" },
    "&:hover svg": { color: COLORS.primary },
  },
  itemActive: {
    background: COLORS.primarySoft,
    color: COLORS.primaryDark,
    fontWeight: 700,
    "&:hover": { background: COLORS.primarySoft },
    "& svg": { color: COLORS.primary },
  },
  itemParentActive: { color: COLORS.primaryDark, fontWeight: 700, "& svg": { color: COLORS.primary } },
  chevron: {
    marginLeft: "auto",
    transition: "transform 0.2s",
    "&&": { fontSize: 20 },
  },
  chevronOpen: { transform: "rotate(180deg)" },
  subList: {
    margin: "0 0 4px 22px",
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${COLORS.borderSoft}`,
  },
  subItem: { height: 34, fontSize: 13.5, paddingLeft: theme.spacing(1.5) },
  itemBack: {
    height: "auto",
    minHeight: 48,
    padding: theme.spacing(0.75, 1.5),
    whiteSpace: "normal",
    lineHeight: 1.25,
    alignItems: "center",
  },
  backText: { display: "flex", flexDirection: "column", minWidth: 0, textAlign: "left" },
  backTarget: {
    fontSize: 12.5,
    fontWeight: 500,
    opacity: 0.9,
    overflowWrap: "anywhere",
  },
  itemPrimary: {
    background: COLORS.primary,
    color: "#fff",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    "&:hover": { background: COLORS.primaryDark, color: "#fff" },
    "& svg, &:hover svg": { color: "#fff" },
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing(1.25),
    borderTop: `1px solid ${COLORS.borderSoft}`,
  },
  user: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.5, 1.25, 1),
    minHeight: 40,
  },
  avatar: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: "50%",
    background: COLORS.primarySoft,
    color: COLORS.primaryDark,
    fontWeight: 800,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  userName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#212121",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userRole: { fontSize: 12, color: "#777" },
  collapseBtn: { alignSelf: "flex-end" },
}));

const MAGAZYN_PATHS = [
  "/magazynWarszawaNowyHandling",
  "/magazynWarszawaZwrotyIBledy",
  "/magazynWarszawa",
  "/magazynWarszawaHandling",
  "/magazynWarszawaLists",
  "/raportMagazynWarszawa",
];

const Sidebar = ({
  collapsed,
  onToggle,
  username,
  role,
  isLists,
  isMagazynLists,
  isAdmin,
  isRaportCzasow,
  isRaportMw,
  activePath = "/user",
  back = null,
  backExtras = [],
  onNavigate,
  onLogout,
}) => {
  const classes = useStyles();

  const groups = useMemo(
    () =>
      [
        {
          title: "Odprawy",
          items: [
            { label: "Dashboard", icon: DashboardIcon, path: "/user" },
            { label: "Odprawy Tranzyt", icon: DirectionsCarIcon, path: "/tranzyt" },
            { label: "Archiwum odpraw", icon: ArchiveIcon, path: "/archive" },
            { label: "Archiwum tranzyt", icon: LocalShippingIcon, path: "/archiveTranzyt" },
            { label: "Magazyn Warszawa", icon: StorageIcon, path: "/magazynWarszawaNowyHandling" },
          ],
        },
        {
          title: "Procesy",
          items: [
            { label: "Rejestr wniosków", icon: AppRegistrationIcon, path: "/rejestrWnioskow" },
            { label: "Kontrola wewnętrzna", icon: GavelIcon, path: "/kontrolaWewnetrzna" },
            { label: "Traces", icon: ExtensionIcon, path: "/traces" },
            { label: "Historia zmian", icon: HistoryIcon, path: "/history" },
          ],
        },
        {
          title: "Raporty i narzędzia",
          items: [
            { label: "Kalkulator ilości spraw", icon: CalculateIcon, path: "/calculator" },
            isAdmin && {
              label: "Kalkulator zrealizowane",
              icon: CalculateIcon,
              path: "/calculatorZrealizowan&Przygotowane",
            },
            isRaportCzasow && {
              label: "Raport czasów odpraw",
              icon: TimelineIcon,
              path: "/raportCzasowOdpraw",
            },
            isLists && { label: "Pokaż listy", icon: ListIcon, path: "/list" },
            isAdmin && { label: "Admin panel", icon: AdminPanelSettingsIcon, path: "/adminPanel" },
          ].filter(Boolean),
        },
      ].filter((g) => g.items.length > 0),
    [isAdmin, isLists, isRaportCzasow],
  );

  // Magazyn Warszawa to osobny modul: po wejsciu menu pokazuje tylko jego zakladki
  // i powrot do glownego systemu.
  const inMagazyn = !back && MAGAZYN_PATHS.includes(activePath);
  const magazynItems = [
    { label: "Bieżące zgłoszenia", icon: StorageIcon, path: "/magazynWarszawaNowyHandling" },
    { label: "Zwroty i błędy", icon: AssignmentReturnIcon, path: "/magazynWarszawaZwrotyIBledy" },
    { label: "Archiwum Odprawa Celna", icon: ArchiveIcon, path: "/magazynWarszawa" },
    { label: "Archiwum Handling", icon: Inventory2Icon, path: "/magazynWarszawaHandling" },
    isRaportMw && { label: "Raport czasów", icon: TimelineIcon, path: "/raportMagazynWarszawa" },
    isMagazynLists && { label: "Zarządzanie listą", icon: AppRegistrationIcon, path: "/magazynWarszawaLists" },
  ].filter(Boolean);

  const renderBackButton = (target) => {
    const button = (
      <button
        type="button"
        className={`${classes.item} ${classes.itemPrimary} ${classes.itemBack}`}
        onClick={() => onNavigate(target.path)}
        aria-label={`Powrót do: ${target.label}`}
      >
        <ArrowBackIcon />
        {!collapsed && (
          <span className={classes.backText}>
            <span>Powrót</span>
            <span className={classes.backTarget}>{target.label}</span>
          </span>
        )}
      </button>
    );
    return collapsed ? (
      <Tooltip title={`Powrót do: ${target.label}`} placement="right" arrow>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  // Grupy z podstronami (np. Magazyn Warszawa): domyslnie rozwiniete, gdy aktywna jest ktoras podstrona.
  const [openGroups, setOpenGroups] = useState({});

  const renderGroupItem = (item) => {
    const Icon = item.icon;
    const childActive = item.children.some((child) => child.path === activePath);
    const open = openGroups[item.path] ?? childActive;
    const toggle = () => setOpenGroups((prev) => ({ ...prev, [item.path]: !open }));
    const parent = (
      <button
        type="button"
        className={`${classes.item} ${childActive ? classes.itemParentActive : ""}`}
        onClick={collapsed ? () => onNavigate(item.children[0].path) : toggle}
        aria-expanded={collapsed ? undefined : open}
      >
        <Icon />
        {!collapsed && <span>{item.label}</span>}
        {!collapsed && (
          <ExpandMoreIcon className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`} />
        )}
      </button>
    );
    return (
      <div key={item.path}>
        {collapsed ? (
          <Tooltip title={item.label} placement="right" arrow>
            {parent}
          </Tooltip>
        ) : (
          parent
        )}
        {!collapsed && open && (
          <div className={classes.subList}>
            {item.children.map((child) => (
              <button
                key={child.path}
                type="button"
                className={`${classes.item} ${classes.subItem} ${
                  child.path === activePath ? classes.itemActive : ""
                }`}
                onClick={() => onNavigate(child.path)}
                aria-current={child.path === activePath ? "page" : undefined}
              >
                <span>{child.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (item, extraClass = "") => {
    if (item.children) return renderGroupItem(item);
    const Icon = item.icon;
    const button = (
      <button
        key={item.path}
        type="button"
        className={`${classes.item} ${item.path === activePath ? classes.itemActive : ""} ${extraClass}`}
        onClick={() =>
          item.path === "__logout" ? onLogout() : onNavigate(item.path)
        }
        aria-current={item.path === activePath ? "page" : undefined}
      >
        <Icon />
        {!collapsed && <span>{item.label}</span>}
      </button>
    );
    return collapsed ? (
      <Tooltip key={item.path} title={item.label} placement="right" arrow>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <aside className={classes.sidebar} style={{ width }} aria-label="Nawigacja">
      <div className={classes.brand}>
        <div className={classes.brandMark}>
          <DashboardIcon />
        </div>
        {!collapsed && <span className={classes.brandText}>DutyStation</span>}
      </div>

      <nav className={classes.nav}>
        {back ? (
          <>
          {renderBackButton(back)}
          {backExtras.map((item) => renderItem(item))}
          </>
        ) : inMagazyn ? (
          <>
            {renderBackButton({ path: "/user", label: "Główny system" })}
            {renderItem({ label: "Dodaj zgłoszenie", icon: AddIcon, path: "/form" }, classes.itemPrimary)}
            {!collapsed && <div className={classes.groupTitle}>Magazyn Warszawa</div>}
            {magazynItems.map((item) => renderItem(item))}
          </>
        ) : (
          <>
        {renderItem(
          { label: "Dodaj zgłoszenie", icon: AddIcon, path: "/form" },
          classes.itemPrimary,
        )}
        {groups.map((group, index) => (
          <div key={group.title}>
            {collapsed ? (
              index > 0 && <div className={classes.groupDivider} />
            ) : (
              <div className={classes.groupTitle}>{group.title}</div>
            )}
            {group.items.map((item) => renderItem(item))}
          </div>
        ))}
          </>
        )}
      </nav>

      <div className={classes.footer}>
        {!collapsed && (
          <div className={classes.user}>
            <div className={classes.avatar}>
              {(username || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className={classes.userName}>{username}</div>
              {role && <div className={classes.userRole}>{role}</div>}
            </div>
          </div>
        )}
        {renderItem({ label: "Wyloguj", icon: LogoutIcon, path: "__logout" })}
        <IconButton
          size="small"
          className={classes.collapseBtn}
          onClick={onToggle}
          aria-label={collapsed ? "Rozwiń menu" : "Zwiń menu"}
          style={{ alignSelf: collapsed ? "center" : "flex-end" }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </div>
    </aside>
  );
};

export default Sidebar;
